<template>
    <div class="flex flex-wrap p-8 bg-white rounded-md shadow overflow-hidden">
        <div class="pb-8 w-full">
            <section>
                <h4 class="w-full text-center text-lg leading-6 font-medium text-gray-900 mb-4">
                    {{title}}
                </h4>
                <div class="flex flex-col">
                    <div class="grid gap-4 lg:grid-cols-4 sm:grid-cols-2">
                        <radio
                            v-for="(option, index) in options"
                            :key="index"
                            v-model="form.period"
                            type="radio"
                            radio-class="hidden"
                            :class="{ 'filter-active-btn': form.period === option.value }"
                            class="flex mt-2 bg-gray-100 hover:bg-gray-200 text-base font-medium py-2 px-6 btn-indigo watchman-radio-btn"
                            :value="option.value"
                            :label="option.label"
                            @update:modelValue="updatePeriod(option.value)"
                        />
                    </div>

                    <date-picker
                        v-if="!hideAdditional"
                        v-model="form.date"
                        :disabledDays="[]"
                        class="mt-6 w-full lg:w-1/4"
                        label="Або оберіть дату"
                        @update:modelValue="form.period=null"
                    />
                </div>
                <div>
                    <button
                        v-if="withRefreshButton"
                        class="btn-indigo mt-3 mr-3"
                        @click.prevent="clearFilters"
                    >
                        Очистити усі фільтри
                    </button>
                    <button
                        v-if="withDownloadButton"
                        class="btn-indigo mt-3"
                        @click.prevent="downloadOrders"
                    >
                        Скачати замовлення
                    </button>
                </div>
            </section>
            <div v-if="!hideAdditional" class="mt-8">
                <Accordion :items="accordionItems">
                    <template v-for="(item, index) in accordionItems" v-slot:content-{{index}}>
                        <section>
                            <div class="flex items-center flex-col px-8 py-4">
                                <div class="flex w-full flex-col lg:flex-row">
                                    <text-input
                                        v-model="form.name"
                                        type="text"
                                        class="pt-4 mr-6 w-full lg:w-1/2"
                                        label="Імя"
                                    />

                                    <text-input
                                        v-model="form.phone"
                                        type="text"
                                        class="pt-4 w-full lg:w-1/2"
                                        label="Телефон"
                                    />
                                </div>
                                <div class="flex w-full flex-col lg:flex-row">
                                    <select-input
                                        v-model="form.lakeId"
                                        class="pt-4 mr-6 w-full lg:w-1/2"
                                        label="Озеро"
                                    >
                                        <option :value="null">Всі</option>
                                        <option
                                            v-for="lake in lakes"
                                            :key="lake.id"
                                            :value="lake.id"
                                        >
                                            {{ lake.name }}
                                        </option>
                                    </select-input>

                                    <select-input
                                        v-model="form.pointId"
                                        class="pt-4 w-full lg:w-1/2"
                                        label="Місце"
                                        :disabled="!form.lakeId"
                                    >
                                        <option :value="null">Всі</option>
                                        <option
                                            v-for="point in points"
                                            :key="point.id"
                                            :value="point.id"
                                        >
                                            {{ point.name }}
                                        </option>
                                    </select-input>
                                </div>
                                <div class="flex w-full flex-col lg:flex-row">
                                    <select-input
                                        v-model="form.typeId"
                                        class="pt-4 mr-6 w-full"
                                        label="Тип рибалки"
                                    >
                                        <option :value="null">Всі</option>
                                        <option
                                            v-for="type in types"
                                            :key="type.id"
                                            :value="type.id"
                                        >
                                            {{ type.name }} ({{ type.description }})
                                        </option>
                                    </select-input>
                                    <select-input
                                        v-model="form.categoryId"
                                        class="pt-4 w-full"
                                        label="Тариф рибалки"
                                    >
                                        <option :value="null">Всі</option>
                                        <option
                                            v-for="category in categories"
                                            :key="category.id"
                                            :value="category.id"
                                        >
                                            {{ category.name }}
                                        </option>
                                    </select-input>
                                </div>
                            </div>
                        </section>
                    </template>
                </Accordion>
            </div>
            <text-input
                v-if="!hideAdditional"
                v-model="form.orderNumber"
                type="text"
                class="w-full mt-4"
                placeholder="Пошук за номером заказу"
                @input="applyFilter"
            />
        </div>
    </div>
</template>

<script>
import LoadingButton from "@/Shared/LoadingButton"
import Layout from '@/Shared/Layout'
import Icon from "@/Shared/Icon"
import Radio from "@/Shared/Radio"
import TextInput from "@/Shared/TextInput"
import DatePicker from "@/Shared/DatePicker"
import SelectInput from "@/Shared/SelectInput"
import axios from "axios"
import throttle from "lodash/throttle"
import Accordion from "@/Component/Accordion.vue"

export default {
    name: "FiltersModal",
    components: {
        Accordion,
        TextInput,
        Radio,
        LoadingButton,
        Icon,
        DatePicker,
        SelectInput
    },
    layout: Layout,
    props: {
        show: Boolean,
        title: String,
        withRefreshButton: {
            type: Boolean,
            default: true,
        },
        withDownloadButton: {
            type: Boolean,
            default: false,
        },
        params: {
            type: Object,
            default: () => ({}),
        },
        hideAdditional: {
            type: Boolean,
            default: false,
        },
        tomorrowButton: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            accordionItems: [
                {title: 'Додаткові фільтри'}
            ],
            form: this.loadFilters(),
            types: [],
            categories: [],
            lakes: [],
            points: [],
            throttledHandler: throttle(function () {
                this.$emit('update:form', this.form)
            }, 150)
        }
    },
    watch: {
        form: {
            deep: true,
            handler(newVal) {
                this.saveFilters(newVal)
                this.throttledHandler()
            }
        },
        'form.lakeId'() {
            this.fetchPoints()
        },
    },
    computed: {
        options() {
            let options = [
                { value: 'today', label: 'Сьогодні' },
                { value: 'yesterday', label: 'Вчора' }
            ]
            if (!this.hideAdditional) {
                options = options.concat([
                    { value: 'lastWeek', label: 'Цей тиждень' },
                    { value: 'lastMonth', label: 'Поточний місяць' }
                ])
            }
            if (this.tomorrowButton) {
                options = options.concat([
                    { value: 'tomorrow', label: 'Завтра' }
                ])
            }

            return options;
        }
    },
    methods: {
        loadFilters() {
            const defaultFilters = {
                orderNumber: null,
                period: 'lastWeek',
                date: [],
                name: '',
                phone: '',
                lakeId: null,
                pointId: null,
                typeId: null,
                categoryId: null
            }
            let savedFilters = null
            if (this.$page.props.currentUrl !== this.$route('dashboard.watchman')) {
                savedFilters = localStorage.getItem('filters')
            }
            return savedFilters ? JSON.parse(savedFilters) : defaultFilters
        },
        saveFilters(filters) {
            localStorage.setItem('filters', JSON.stringify(filters));
        },
        updatePeriod(value) {
            this.form.period = value
            this.form.date = []
        },
        async applyFilter() {
            this.$emit('update:form', this.form)
        },
        async fetchData() {
            await Promise.all([
                this.fetchTypes(),
                this.fetchCategories(),
                this.fetchLakes(),
            ]).then(() => {
                this.updateFormValue()
            })
        },
        async fetchTypes() {
            const response = await axios.get(this.$route('all.types'))
            this.types = await response.data.types;
        },
        async fetchCategories() {
            const response = await axios.get(this.$route('all.categories'))
            this.categories = await response.data.categories;
        },
        async fetchLakes() {
            const response = await axios.get(this.$route('all.lakes'))
            this.lakes = await response.data.lakes;
            await this.fetchPoints()
        },
        async fetchPoints() {
            this.points = []
            if (this.form.lakeId) {
                const savedFilters = this.loadFilters()
                if (savedFilters.pointId !== this.form.pointId) {
                    this.form.pointId = null
                }
                const response = await axios.get(this.$route('all.points', this.form.lakeId))
                this.points = await response.data.points;
            }
        },
        updateFormValue() {
            if (this.params) {
                this.form = this.params
            }
        },
        clearFilters() {
            this.form = {
                orderNumber: null,
                period: 'lastWeek',
                date: [],
                name: '',
                phone: '',
                lakeId: null,
                pointId: null,
                typeId: null,
                categoryId: null
            }
            localStorage.removeItem('filters')
            this.fetchData()
        },
        downloadOrders() {
            this.$emit('download')
        }
    },
    mounted() {
        this.fetchData()
        this.loadFilters()
        this.throttledHandler()
    }
}
</script>

